import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  DocketEntry,
  FileProceedingsDto,
  ProceedingsRecord,
  ResolveProceedingsDto,
  UpdateProceedingsDto,
} from '@munifocus/contracts-gov';
import { api } from './api-client';
import { useMutationToast } from './mutation-toast';
import { filesKeys } from './files-api';
import { deadlinesKeys } from './deadlines-keys';

// --- raw API calls ---------------------------------------------------------

export async function listProceedingsForFile(fileId: string): Promise<ProceedingsRecord[]> {
  const { data } = await api.get<ProceedingsRecord[]>(`/files/${fileId}/proceedings`);
  return data;
}

export async function fileProceedings(
  fileId: string,
  dto: FileProceedingsDto,
): Promise<ProceedingsRecord> {
  const { data } = await api.post<ProceedingsRecord>(`/files/${fileId}/proceedings`, dto);
  return data;
}

/** P3 — write post-filing case management onto a filed matter. */
export async function updateProceedings(
  id: string,
  dto: UpdateProceedingsDto,
): Promise<ProceedingsRecord> {
  const { data } = await api.patch<ProceedingsRecord>(`/proceedings/${id}`, dto);
  return data;
}

/** Record the court's outcome on a filed matter — it leaves the open docket. */
export async function resolveProceedings(
  id: string,
  dto: ResolveProceedingsDto,
): Promise<ProceedingsRecord> {
  const { data } = await api.post<ProceedingsRecord>(`/proceedings/${id}/resolution`, dto);
  return data;
}

export async function listDocket(): Promise<DocketEntry[]> {
  const { data } = await api.get<DocketEntry[]>('/proceedings/docket');
  return data;
}

/** Docket export format. */
export type DocketFormat = 'xlsx' | 'docx';

/**
 * Download the court docket as Word or Excel. The endpoint streams a binary with
 * a `Content-Disposition` attachment header; we materialise it client-side and
 * click a transient anchor so the browser saves it.
 */
export async function downloadDocket(format: DocketFormat, lang = 'en'): Promise<void> {
  // `lang` is the officer's interface language; the export's headings, statuses
  // and pleas are printed in it.
  const fr = lang.startsWith('fr');
  const res = await api.get('/proceedings/docket/export', {
    params: { format, lang: fr ? 'fr' : 'en' },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data as Blob);
  try {
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `${fr ? 'role-du-tribunal' : 'court-docket'}.${format}`;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

// --- React Query hooks -----------------------------------------------------

export const proceedingsKeys = {
  docket: ['proceedings', 'docket'] as const,
  forFile: (fileId: string) => ['proceedings', 'file', fileId] as const,
};

/** Proceedings filed against a single file (drives the Step 9 panel). */
export function useProceedingsForFile(fileId: string | undefined) {
  return useQuery({
    queryKey: proceedingsKeys.forFile(fileId ?? ''),
    queryFn: () => listProceedingsForFile(fileId as string),
    enabled: !!fileId,
  });
}

/** The full court docket (Court Docket route). */
export function useDocket() {
  return useQuery({ queryKey: proceedingsKeys.docket, queryFn: listDocket });
}

/**
 * P3 — update the case-management fields on one docket row. Invalidates the
 * docket and (so the Step 9 panel stays truthful) that matter's file.
 */
export function useUpdateProceedings(id: string | undefined, fileId?: string) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: (dto: UpdateProceedingsDto) => {
      if (!id) throw new Error('useUpdateProceedings: called without a proceedings id');
      return updateProceedings(id, dto);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: proceedingsKeys.docket });
      if (fileId) qc.invalidateQueries({ queryKey: proceedingsKeys.forFile(fileId) });
      toast.success('gov.proceedings.savedToast');
    },
    onError: (err) => toast.error(err, 'gov.proceedings.saveError'),
  });
}

export function useFileProceedings(fileId: string | undefined) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: (dto: FileProceedingsDto) => fileProceedings(fileId as string, dto),
    onSuccess: () => {
      if (fileId) qc.invalidateQueries({ queryKey: proceedingsKeys.forFile(fileId) });
      qc.invalidateQueries({ queryKey: proceedingsKeys.docket });
      qc.invalidateQueries({ queryKey: filesKeys.all });
      // A filing starts nothing new, but it ends the six-month limitation
      // clock's relevance — and the list should say so.
      void qc.invalidateQueries({ queryKey: deadlinesKeys.all });
      toast.success('gov.proceedings.filedToast');
    },
    // Duplicate court number, closed file: the API names it; the panel's
    // inline alert stays for the generic case.
    onError: (err) => toast.error(err, 'gov.proceedings.fileError'),
  });
}

/**
 * Step 9's outcome, recorded on the matter itself.
 *
 * Saving "resolved" or "unresolved" on the step used to touch only the file's
 * stage data, so the matter stayed `filed` on the court docket forever — and a
 * file returned to council could never file again, because the step read "any
 * matter exists" as "already filed".
 */
export function useResolveProceedings(fileId: string | undefined) {
  const qc = useQueryClient();
  const toast = useMutationToast();
  return useMutation({
    mutationFn: ({ id, outcome }: { id: string; outcome: ResolveProceedingsDto['outcome'] }) =>
      resolveProceedings(id, { outcome }),
    onSuccess: () => {
      if (fileId) qc.invalidateQueries({ queryKey: proceedingsKeys.forFile(fileId) });
      qc.invalidateQueries({ queryKey: proceedingsKeys.docket });
      void qc.invalidateQueries({ queryKey: deadlinesKeys.all });
    },
    onError: (err) => toast.error(err, 'gov.proceedings.resolveError'),
  });
}
